<template>
  <div class="content">
    <Hero
        :image="getStrapiImageLocation(data?.image) || ''"
        image-provider="strapi"
        extra-content-classes="vcenter grid--two"
        :vertical-center="true"
        small
      >
      <template #left>
        <h2 class="heading--med text--white text--bold no-lh" style="margin-bottom: 0.87rem;">{{ data?.title }}</h2>
        <h4 class="text--l text--white md-1">{{ data?.subtitle }}</h4>
      </template>
    </Hero>
    <div class="block block--gray mt-25">
      <div class="maxw--inner mb-2">
        <h3 class="heading--med text--primary text--bold">{{ data?.introductionHeading }}</h3>
        <span class="text--primary text--with-icon">
          <Clock class="icon icon--s"/>
          <span>{{ data?.timeText }} per week</span>
        </span>
      </div>
      <div class="maxw--inner grid-text-img">
        <div class="text--m">
          <StrapiBlocksText :nodes="data?.introductionText || []"/>
        </div>
        <div>
          <div class="image--holder">
            <NuxtImg :src="getStrapiImageLocation(data?.introductionImage) || ''" provider="strapi"/>
          </div>
          <div class="block block--white block--angled block--angled-r" id="bpoints">
            <h5 class="heading-6 text--secondary mb-1">{{ data?.mainPointHeading }}</h5>
            <ul class="list list--dots list--indented mb-2">
              <li class="list__item" v-for="point in data?.mainPoints" :key="point.id">{{ point.value }}</li>
            </ul>
            <a href="#contact" class="btn btn--accent">Solliciteer</a>
          </div>
        </div>
      </div>
    </div>
    <div class="block block--top-gray block--bottom-accent block--ynone">
      <div class="block block--white maxw--inner block--px-l">
        <!-- <h2 class="heading-2 text--secondary text--light mb-1">{{ data?.middle?.heading }}</h2> -->
        <template v-for="item in data?.middle?.items" :key="item">
          <h5 class="heading-6 text--primary">{{ item.heading }}</h5>
          <div class="text--m last-nm">
            <StrapiBlocksText :nodes="item.content || []"/>
          </div>
        </template>
      </div>
    </div>
    <div class="block block--accent">
      <div class="maxw--inner">
        <h2 class="heading-2--med text--white text--center" v-html="addBreakToBold(data?.benefits?.heading || '')"></h2>
      </div>
    </div>
    <div class="block block--top-accent block--ynone">
      <div class="block block--white maxw--inner block--px-l">
        <div class="grid--med-gap grid--two">
          <div>
            <!-- <ul class="list list--dots mb-2">
              <li class="list__item text--l text--bold" v-for="point in data?.benefits.points" :key="point.id">{{ point.value }}</li>
            </ul> -->
            <div class="image--holder mb-2" v-if="data?.benefits?.image?.data">
              <NuxtImg :src="getStrapiImageLocation(data?.benefits.image) || ''"/>
            </div>
            <div class="text--m">
              <StrapiBlocksText :nodes="data?.benefits.content || []"/>
            </div>
          </div>
          <div class="text--m">
            <StrapiBlocksText :nodes="data?.benefits.contentTwo || []"/>
          </div>
        </div>
        <div class="qoute text--center no-m" style="margin-top: 1.5rem;">
          <StrapiBlocksText :nodes="data?.benefits.bottomQoute || []"/>
        </div>
        <div class="centered mb-2" style="margin-top: 1.5rem;">
          <nuxt-link href="#contact" class="btn btn--accent btn--large">Solliciteer</nuxt-link>
        </div>
      </div>
    </div>
    <div class="block block--secondary">
      <div class="maxw--inner">
        <h6 class="subheader text--white text--center mb-1">{{ data?.fiveSteps?.subheading }}</h6>
        <h2 class="heading-1 text--white text--center mb-2" v-html="addBreakToBold(data?.fiveSteps?.heading || '')"></h2>
        <div class="grid--five">
          <StepItem 
            v-for="item, i in data?.fiveSteps?.steps || []" 
            :key="i"
            :icon="getStrapiImageLocation(item?.image)"
            :title="item.heading"
            :text="item.text"
          />
        </div>
      </div>
    </div>
    <div class="block" v-if="experienceStory">
      <div class="maxw--inner">
        <h2 class="heading-2 text--center mb-2 text--primary text--cramped">{{ data?.experienceStoryHeader }}</h2>
        <div class="block block--white card card--p-norm grid--image-left">
            <div class="image--holder image--left">
              <NuxtImg :src="getStrapiImageLocation(experienceStory?.thumbnail) || ''" provider="strapi"/>
            </div>
          <div class="block__column">
            <h4 class="text--l text--bold text--primary mb-1">{{ experienceStory?.preTitle }} {{ experienceStory?.title }}</h4>
            <div class="text--m">
              <StrapiBlocksText :nodes="experienceStory?.previewText || []"/>
            </div>
            <nuxt-link :to="`/ervaringsverhalen/${experienceStory?.slug}`" class="btn btn--secondary">Lees meer</nuxt-link>
          </div>
        </div>
      </div>
    </div>
    <div class="block" v-if="data?.form">
      <FormContact 
        class="block"
        id="contact"
        :data="data?.form"
        :page-id="data?.id"
        identifier="vacancy-page"
        :name="data?.title"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import Clock from '~/assets/svg/clock.svg?component';
import type StoryPage from '~/typings/strapi/pages/storyPage';
import type VacancyPage from '~/typings/strapi/pages/vacancyPage';

const route = useRoute();
const { findOne, find } = useStrapi();

const { data, pending } = await useAsyncData(
  'vacancyPage',
  () => findOne<VacancyPage>(`slugify/slugs/vacancy-page/${route.params.slug}`, {
    populate: [
      'introductionImage', 'mainPoints', 
      'middle', 'middle.items',
      'benefits', 'benefits.points', 'benefits.image',
      'image',
      'fiveSteps', 'fiveSteps.steps', 'fiveSteps.steps.image',
      'form',
      'seo', 'seo.metaImage',
      'linkedStory', 'linkedStory.thumbnail'
    ]
  }),
  {
    transform: flattenObject
  }
);

if (!data.value) {
  throw createError({ statusCode: 404, message: 'Vacature niet gevonden' });
}

useHead({
  titleTemplate: '%s | Van Eerd - Werken bij',
  title: 'Vacature ' + data.value?.title,
});

useSeoMeta({
  description: data.value?.seo?.metaDescription,
  ogTitle: data.value?.seo?.metaTitle || data.value?.title,
  ogDescription: data.value?.seo?.metaDescription,
  ogImage: getStrapiImageLocation(data.value?.seo?.metaImage, true) || getStrapiImageLocation(data.value?.image, true),
  ogUrl: 'https://werkenbijvaneerd.nl/vacatures/' + data.value?.slug,
  twitterTitle: data.value?.seo?.metaTitle || data.value?.title,
  twitterDescription: data.value?.seo?.metaDescription,
  twitterImage: getStrapiImageLocation(data.value?.seo?.metaImage, true) || getStrapiImageLocation(data.value?.image, true),
  twitterCard: 'summary_large_image',
})

const experienceStory = ref<StoryPage | null>(null);

watchEffect(async () => {
  if (!pending.value) {
    if (typeof data.value?.linkedStory !== "number" && data.value?.linkedStory?.data) {
      experienceStory.value = flattenObject(data?.value.linkedStory);
    } else {
      // If no linked story is found, get latest one
      const res = await find<StoryPage>('story-pages', { 
        pagination: { limit: 1, start: 0},
        sort: 'publishedAt:desc',
        populate: ['thumbnail']
      });

      const resFormatted = flattenCollection(res);
      experienceStory.value = resFormatted.length > 0 ? resFormatted[0] : null;
    }
  }
})
</script>
